import Phaser from 'phaser';
import { GAME_FONT_FAMILY, GOLD_TEXT_COLOR, ITEM_RARITY_COLORS, ItemRarity } from '@shared/constants';
import type {
  SpellVendorEntryView,
  SpellVendorOpenedMessage,
} from '@shared/types/messages';
import { CHARACTER_CLASSES, CHARACTER_RACES, type CharacterClassId, type CharacterRaceId } from '@shared/types/character';
import { getSpellDefinition } from '@shared/data/spells';
import { getItemTemplate } from '@shared/data/items';
import type { ItemDefinition } from '@shared/data/items';
import type { SpellDefinition } from '@shared/types/spell';
import { getIconTexture, createCloseButton, formatThousands } from '../../sprites/IconAtlases';
import { getSpellInfoRows } from './spellInfoRows';
import { ItemTooltip } from './ItemTooltip';

function nominalRarity(tmpl: ItemDefinition): number {
  if (tmpl.unique) return ItemRarity.LEGENDARY;
  if (tmpl.setId) return ItemRarity.SET;
  return ItemRarity.COMMON;
}

const FALLBACK_RARITY_COLOR = 0x999999;

const PLACEHOLDER_ICON = 'item_icon_noicon';
const THUMB_SIZE = 16;
const DETAIL_ICON_SIZE = 48;

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

function resolveSpellIcon(scene: Phaser.Scene, iconKey: string | undefined): string {
  if (iconKey && scene.textures.exists(iconKey)) return iconKey;
  return PLACEHOLDER_ICON;
}

function classLabel(id: CharacterClassId): string {
  return CHARACTER_CLASSES.find((c) => c.id === id)?.labelEs ?? id;
}

function raceLabel(id: CharacterRaceId): string {
  return CHARACTER_RACES.find((r) => r.id === id)?.labelEs ?? id;
}

interface InventoryRow {
  slotIndex: number;
  itemId: string;
  quantity: number;
  rolledRarity?: number;
  rolledAffixes?: Record<string, Array<{ v: number; t: number }>>;
}

export interface SpellVendorPanelCallbacks {
  onBuy: (spellId: string) => void;
  onClose: () => void;
  onMove?: (x: number, y: number) => void;
  /**
   * Click sobre el icono de un item requerido. El cliente puede buscar en
   * el inventario y hacer un highlight visual del slot que lo contiene.
   */
  onHighlightInventorySlot?: (itemId: string) => void;
}

export interface SpellVendorPanelOptions {
  initialX: number;
  initialY: number;
  width?: number;
  height?: number;
}

interface ListRowUI {
  bg: Phaser.GameObjects.Rectangle;
  icon: Phaser.GameObjects.Image;
  text: Phaser.GameObjects.Text;
  /** Icono pequeño "ya aprendido" pegado a la derecha del nombre. */
  learnedIcon: Phaser.GameObjects.Sprite;
}

const DEPTH_BG = 1100;
const DEPTH_FG = 1101;
const DEPTH_BTN = 1102;
const ALL_CLASSES_FILTER = '__all__';

/**
 * Panel del vendedor de hechizos. Dos columnas:
 *  - IZQ: lista de spells del vendor (icon + nombre). Filtro de clase arriba.
 *  - DER: detalle del spell seleccionado (icon grande, nombre, descripción,
 *    costos en oro + items, gating de clase, botón Aprender).
 *
 * Reglas de filtro:
 *  - "Todas las clases" muestra todo lo que el vendor enseña.
 *  - Clase X (sólo se listan las que el vendor tiene en algún allowedClasses):
 *    muestra entries con allowedClasses incluyendo X + entries sin allowedClasses
 *    (globales o forbidden-only — siempre visibles porque la clase no está vetada
 *    por whitelist).
 */
export class SpellVendorPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: SpellVendorPanelCallbacks;
  private readonly pw: number;
  private readonly ph: number;
  private readonly visibleRows = 12;
  private readonly rowH = 22;

  private panelX: number;
  private panelY: number;

  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private currentNpcId: string | null = null;
  private currentVendorId: string | null = null;
  private currentVendorName = '';
  private currentEntries: SpellVendorEntryView[] = [];
  private currentInventory: InventoryRow[] = [];
  private currentGold = 0;
  private selectedSpellId: string | null = null;
  private scrollOffset = 0;
  private classFilter: string = ALL_CLASSES_FILTER;

  // Persistent UI elements
  private bg!: Phaser.GameObjects.Rectangle;
  private title!: Phaser.GameObjects.Text;
  private goldLabel!: Phaser.GameObjects.Container;
  private goldLabelIcon!: Phaser.GameObjects.Sprite;
  private goldLabelText!: Phaser.GameObjects.Text;
  private closeBtn!: Phaser.GameObjects.Sprite;
  private listBg!: Phaser.GameObjects.Rectangle;
  private scrollUp!: Phaser.GameObjects.Text;
  private scrollDown!: Phaser.GameObjects.Text;
  private listRows: ListRowUI[] = [];

  // Detail UI
  private detailIcon!: Phaser.GameObjects.Image;
  private detailName!: Phaser.GameObjects.Text;
  /** Icono "ya aprendido" pegado a la derecha del nombre. Placeholder = 'locked'. */
  private detailLearnedIcon!: Phaser.GameObjects.Sprite;
  private detailDesc!: Phaser.GameObjects.Text;
  private detailMeta!: Phaser.GameObjects.Text;
  /**
   * Bloque de stats del spell: maná, cooldown, target type, rango, área, cast.
   * Va entre la descripción y los costos. Renderizado como múltiples líneas
   * (una por stat) con iconos unicode prefijo.
   */
  /**
   * Container con stats del spell (Maná, CD, Target, Rango, Área, Cast).
   * Cada fila tiene sprite + texto. Se reconstruye en `refreshDetail`.
   * Posición = `(rx, detTop + DETAIL_ICON_SIZE + 84)`.
   */
  private detailSpellInfo!: Phaser.GameObjects.Container;
  private detailSpellInfoX = 0;
  private detailSpellInfoY = 0;
  private detailClassBadge!: Phaser.GameObjects.Text;
  private detailRaceBadge!: Phaser.GameObjects.Text;
  /**
   * Precio en oro del spell, formato inline "Precio: N oro". Posicionado
   * arriba de la descripcion porque es un dato fijo (siempre 1 linea) — la
   * descripcion y las caracteristicas son variables. Misma convencion que
   * `MerchantPanel.detailPrice`.
   */
  private detailPrice!: Phaser.GameObjects.Container;
  private detailPriceLabel!: Phaser.GameObjects.Text;
  private detailPriceIcon!: Phaser.GameObjects.Sprite;
  private detailPriceAmount!: Phaser.GameObjects.Text;
  private learnBtn!: Phaser.GameObjects.Text;
  private learnHint!: Phaser.GameObjects.Text;

  // Filter chips (recreated each open: depend on vendor catalog).
  // Cuando no entran en el ancho del panel se paginan: cada chip guarda su
  // página y sólo se muestra la activa, con flechas ‹ › para navegar.
  private filterChips: Array<{ text: Phaser.GameObjects.Text; page: number }> = [];
  private filterArrowLeft: Phaser.GameObjects.Text | null = null;
  private filterArrowRight: Phaser.GameObjects.Text | null = null;
  private chipPage = 0;
  private chipPageCount = 1;

  /**
   * Iconos de items requeridos del spell seleccionado. Reconstruidos en cada
   * refreshDetail. Cada elemento es {image, badge, itemId, qty}.
   */
  private itemCostIcons: Array<{
    image: Phaser.GameObjects.Image;
    badge: Phaser.GameObjects.Text;
    itemId: string;
    qty: number;
  }> = [];
  /** Fondos rectangulares con borde de rareza para los iconos de itemCosts. */
  private itemCostBgRects: Phaser.GameObjects.Rectangle[] = [];
  private tooltip!: ItemTooltip;

  constructor(
    scene: Phaser.Scene,
    callbacks: SpellVendorPanelCallbacks,
    options: SpellVendorPanelOptions
  ) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.pw = options.width ?? 520;
    this.ph = options.height ?? 380;
    this.panelX = options.initialX;
    this.panelY = options.initialY;
    this.tooltip = new ItemTooltip(scene);
    this.buildPanel();
    this.setVisible(false);
  }

  // === PUBLIC API ============================================================

  open(message: SpellVendorOpenedMessage, gold: number, inventory: InventoryRow[]): void {
    this.currentNpcId = message.npcId;
    this.currentVendorId = message.vendorId;
    this.currentVendorName = message.vendorName;
    this.currentEntries = message.entries;
    this.currentGold = gold;
    this.currentInventory = inventory;
    this.classFilter = ALL_CLASSES_FILTER;
    this.scrollOffset = 0;
    this.chipPage = 0;
    this.selectedSpellId = message.entries[0]?.spellId ?? null;
    this.title.setText(message.vendorName);
    this.rebuildFilterChips();
    this.setVisible(true);
    this.isOpen = true;
    this.updateGoldLabel();
    this.refreshList();
    this.refreshDetail();
  }

  /** Actualiza el snapshot de entries (ej. post-compra). Mantiene selección. */
  setEntries(entries: SpellVendorEntryView[]): void {
    this.currentEntries = entries;
    if (!this.isOpen) return;
    this.rebuildFilterChips();
    this.refreshList();
    this.refreshDetail();
  }

  setGold(gold: number): void {
    this.currentGold = gold;
    if (!this.isOpen) return;
    this.updateGoldLabel();
    this.refreshDetail();
    this.refreshList();
  }

  setInventory(inventory: InventoryRow[]): void {
    this.currentInventory = inventory;
    if (!this.isOpen) return;
    this.refreshDetail();
    this.refreshList();
  }

  /**
   * Cierra el panel. Si silent=true, no dispara onClose (útil cuando el server
   * ya cerró el vendor — ej. el player abrió otro panel y CLOSE_VENDOR sería redundante).
   */
  close(silent: boolean = false): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.clearItemCostIcons();
    this.setVisible(false);
    // Disparar onClose ANTES de limpiar currentNpcId — el callback necesita
    // el id para enviar CLOSE_VENDOR al server (si no, queda en INTERACTING_NPC).
    if (!silent) this.callbacks.onClose();
    this.currentNpcId = null;
    this.currentVendorId = null;
  }

  isOpenPanel(): boolean { return this.isOpen; }
  isDraggingPanel(): boolean { return this.isDragging; }
  getCurrentNpcId(): string | null { return this.currentNpcId; }

  isPointerOver(p: Phaser.Input.Pointer): boolean {
    if (!this.isOpen) return false;
    const l = this.panelX - this.pw / 2;
    const r = this.panelX + this.pw / 2;
    const t = this.panelY - this.ph / 2;
    const b = this.panelY + this.ph / 2;
    return p.x >= l && p.x <= r && p.y >= t && p.y <= b;
  }

  handlePointerMove(p: Phaser.Input.Pointer): void {
    if (!this.isDragging) return;
    const newX = p.x - this.dragOffsetX;
    const newY = p.y - this.dragOffsetY;
    this.movePanel(newX, newY);
    this.callbacks.onMove?.(this.panelX, this.panelY);
  }
  handlePointerUp(): void { this.isDragging = false; }

  // === BUILD =================================================================

  private buildPanel(): void {
    const { panelX: px, panelY: py, pw, ph } = this;
    const bx = px - pw / 2;
    const by = py - ph / 2;
    const pad = 10;
    const filterRowH = 22;
    const listW = pw * 0.4;
    const detW = pw - listW - pad * 3;
    const lx = bx + pad;
    const rx = lx + listW + pad;

    this.bg = this.scene.add
      .rectangle(px, py, pw, ph, 0x111111, 0.92)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que la ventana no deje pasar clicks ni cambie el cursor.
      .setInteractive();

    this.title = this.scene.add
      .text(px, by + 16, '', withGameFont({ fontSize: '15px', color: '#ffffff', fontStyle: 'bold' }))
      .setOrigin(0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.title.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = p.x - this.panelX;
      this.dragOffsetY = p.y - this.panelY;
      p.event.stopPropagation();
    });

    // Gold label = container con sprite del oro + número.
    const goldTex = getIconTexture('gold');
    this.goldLabelIcon = this.scene.add
      .sprite(0, 0, goldTex?.textureKey ?? '', goldTex?.frame ?? 0)
      .setOrigin(0, 0.5)
      .setDisplaySize(14, 14)
      .setScrollFactor(0);
    this.goldLabelText = this.scene.add
      .text(14, 0, '0', withGameFont({
        fontSize: '12px', color: GOLD_TEXT_COLOR, fontStyle: 'bold',
      }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.goldLabel = this.scene.add
      .container(bx + pad, by + 16, [this.goldLabelIcon, this.goldLabelText])
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    this.closeBtn = createCloseButton(this.scene, bx + pw - 14, by + 14, 14)!
      .setOrigin(1, 0)
      .setDepth(DEPTH_BTN);
    this.closeBtn.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.close();
      p.event.stopPropagation();
    });

    // Filter chips se reconstruyen al open() segun el catalogo del vendor.
    // (no creo nada acá; rebuildFilterChips se encarga.)

    // List bg + scrollers
    const listTopY = by + 28 + filterRowH;
    const listH = ph - (listTopY - by) - pad;
    this.listBg = this.scene.add
      .rectangle(lx + listW / 2, listTopY + listH / 2, listW, listH, 0x1a1a1a, 0.85)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x444444)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0);

    this.scrollUp = this.scene.add
      .text(lx + listW - 6, listTopY + 2, '▲', withGameFont({ fontSize: '11px', color: '#ffffaa' }))
      .setOrigin(1, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.scrollUp.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.scrollOffset = Math.max(0, this.scrollOffset - this.visibleRows);
      this.refreshList();
      p.event.stopPropagation();
    });

    this.scrollDown = this.scene.add
      .text(lx + listW - 6, listTopY + listH - 14, '▼', withGameFont({ fontSize: '11px', color: '#ffffaa' }))
      .setOrigin(1, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.scrollDown.on('pointerup', (p: Phaser.Input.Pointer) => {
      const filtered = this.filteredEntries();
      const max = Math.max(0, filtered.length - this.visibleRows);
      this.scrollOffset = Math.min(max, this.scrollOffset + this.visibleRows);
      this.refreshList();
      p.event.stopPropagation();
    });

    // List rows (slots fijos, contenido se rellena)
    const listStartY = listTopY + 16;
    for (let i = 0; i < this.visibleRows; i++) {
      const y = listStartY + i * this.rowH;
      const rowBg = this.scene.add
        .rectangle(lx + listW / 2, y + this.rowH / 2 - 2, listW - 4, this.rowH - 2, 0x222222, 0)
        .setOrigin(0.5)
        .setDepth(DEPTH_BG + 1)
        .setScrollFactor(0);
      const icon = this.scene.add
        .image(lx + 8 + THUMB_SIZE / 2, y + this.rowH / 2, PLACEHOLDER_ICON)
        .setDisplaySize(THUMB_SIZE, THUMB_SIZE)
        .setOrigin(0.5)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0);
      const txt = this.scene.add
        .text(lx + 8 + THUMB_SIZE + 6, y + 3, '', withGameFont({ fontSize: '11px', color: '#ffffff' }))
        .setOrigin(0, 0)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });

      // Placeholder "learned" icon — se posiciona dinámicamente en refreshList
      // pegado al final del nombre. Visible sólo si entry.alreadyLearned.
      const lockedTex = getIconTexture('locked');
      const learnedIcon = this.scene.add
        .sprite(0, y + this.rowH / 2, lockedTex?.textureKey ?? '', lockedTex?.frame ?? 0)
        .setOrigin(0, 0.5)
        .setDisplaySize(12, 12)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0)
        .setVisible(false);

      const idx = i;
      const onClick = (p: Phaser.Input.Pointer): void => {
        this.handleRowClick(idx);
        p.event.stopPropagation();
      };
      txt.on('pointerup', onClick);
      icon.setInteractive({ useHandCursor: true });
      icon.on('pointerup', onClick);
      rowBg.setInteractive({ useHandCursor: true });
      rowBg.on('pointerup', onClick);

      this.listRows.push({ bg: rowBg, icon, text: txt, learnedIcon });
    }

    // Detail panel
    const detTop = listTopY;
    this.detailIcon = this.scene.add
      .image(rx + DETAIL_ICON_SIZE / 2, detTop + DETAIL_ICON_SIZE / 2 + 6, PLACEHOLDER_ICON)
      .setDisplaySize(DETAIL_ICON_SIZE, DETAIL_ICON_SIZE)
      .setOrigin(0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    const infoX = rx + DETAIL_ICON_SIZE + 8;

    this.detailName = this.scene.add
      .text(infoX, detTop + 6, '', withGameFont({ fontSize: '13px', color: '#ffff88', fontStyle: 'bold' }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    // Icono "ya aprendido" — placeholder = 'locked'. Posición se recalcula en
    // refreshDetail (depende del ancho del nombre). Visible sólo si learned.
    const detailLockedTex = getIconTexture('locked');
    this.detailLearnedIcon = this.scene.add
      .sprite(infoX, detTop + 6 + 9, detailLockedTex?.textureKey ?? '', detailLockedTex?.frame ?? 0)
      .setOrigin(0, 0.5)
      .setDisplaySize(14, 14)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setVisible(false);

    this.detailMeta = this.scene.add
      .text(infoX, detTop + 26, '', withGameFont({ fontSize: '10px', color: '#88aaff' }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    this.detailClassBadge = this.scene.add
      .text(infoX, detTop + 42, '', withGameFont({ fontSize: '9px', color: '#aaaaaa' }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    this.detailRaceBadge = this.scene.add
      .text(infoX, detTop + 54, '', withGameFont({ fontSize: '9px', color: '#aaaaaa' }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    // Precio = container "Precio:" + sprite oro + monto. Layout horizontal
    // izquierda→derecha. Posicionado arriba de la descripcion porque su
    // shape nunca cambia (la descripcion + caracteristicas son variables).
    this.detailPriceLabel = this.scene.add
      .text(0, 0, 'Precio:', withGameFont({
        // Label hereda estilo del panel (blanco) — el dorado se reserva al
        // ícono + monto a su derecha.
        fontSize: '11px', color: '#ffffff', fontStyle: 'bold',
      }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    const detailGoldTex = getIconTexture('gold');
    this.detailPriceIcon = this.scene.add
      .sprite(0, 0, detailGoldTex?.textureKey ?? '', detailGoldTex?.frame ?? 0)
      .setOrigin(0, 0.5)
      .setDisplaySize(14, 14)
      .setScrollFactor(0);
    this.detailPriceAmount = this.scene.add
      .text(0, 0, '0', withGameFont({
        fontSize: '11px', color: GOLD_TEXT_COLOR, fontStyle: 'bold',
      }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.detailPrice = this.scene.add
      .container(rx, detTop + DETAIL_ICON_SIZE + 26, [
        this.detailPriceLabel,
        this.detailPriceIcon,
        this.detailPriceAmount,
      ])
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    this.detailDesc = this.scene.add
      .text(rx, detTop + DETAIL_ICON_SIZE + 34, '', withGameFont({
        fontSize: '10px', color: '#cccccc', wordWrap: { width: detW },
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    // Bloque de stats del spell = Container con sprite + text por línea.
    // Se reconstruye en rebuildSpellInfo. Hasta 6 líneas → ~72px.
    this.detailSpellInfoX = rx;
    this.detailSpellInfoY = detTop + DETAIL_ICON_SIZE + 84;
    this.detailSpellInfo = this.scene.add
      .container(this.detailSpellInfoX, this.detailSpellInfoY, [])
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    this.learnBtn = this.scene.add
      .text(rx + detW / 2, by + ph - 28, '[ Aprender ]', withGameFont({
        fontSize: '12px', color: '#88ff88', backgroundColor: '#333333',
      }))
      .setOrigin(0.5)
      .setPadding(10, 4, 10, 4)
      .setDepth(DEPTH_BTN)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.learnBtn.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (this.selectedSpellId) {
        const entry = this.currentEntries.find((e) => e.spellId === this.selectedSpellId);
        if (entry && this.canAffordEntry(entry)) {
          this.callbacks.onBuy(this.selectedSpellId);
        }
      }
      p.event.stopPropagation();
    });

    this.learnHint = this.scene.add
      .text(rx + detW / 2, by + ph - 12, '', withGameFont({
        fontSize: '9px', color: '#888888',
      }))
      .setOrigin(0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);
  }

  // === FILTER CHIPS ==========================================================

  private uniqueAllowedClasses(): CharacterClassId[] {
    const out = new Set<CharacterClassId>();
    for (const e of this.currentEntries) {
      for (const c of e.allowedClasses ?? []) {
        out.add(c as CharacterClassId);
      }
    }
    return Array.from(out);
  }

  private rebuildFilterChips(): void {
    for (const { text } of this.filterChips) text.destroy();
    this.filterChips = [];
    this.filterArrowLeft?.destroy();
    this.filterArrowRight?.destroy();
    this.filterArrowLeft = null;
    this.filterArrowRight = null;

    const { panelX: px, panelY: py, pw, ph } = this;
    const bx = px - pw / 2;
    const by = py - ph / 2;
    const pad = 10;
    const startX = bx + pad;
    const chipY = by + 32;
    const ARROW_W = 16; // espacio reservado a cada lado para ‹ ›
    const CHIP_GAP = 4;

    const options: Array<{ id: string; label: string }> = [
      { id: ALL_CLASSES_FILTER, label: 'Todas' },
      ...this.uniqueAllowedClasses().map((c) => ({ id: c as string, label: classLabel(c) })),
    ];

    // 1) Crear los chips (en x=0 provisional) para poder medir su ancho real.
    const chips = options.map((opt) => {
      const isActive = this.classFilter === opt.id;
      const text = this.scene.add
        .text(0, chipY, opt.label, withGameFont({
          fontSize: '10px',
          color: isActive ? '#222222' : '#cccccc',
          backgroundColor: isActive ? '#ffd166' : '#333333',
        }))
        .setOrigin(0, 0)
        .setPadding(6, 3, 6, 3)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });
      text.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.classFilter = opt.id;
        this.scrollOffset = 0;
        this.rebuildFilterChips();
        this.refreshList();
        p.event.stopPropagation();
      });
      return { opt, text };
    });

    // 2) Empaquetar greedy en páginas. Primero probamos sin reservar flechas;
    //    si todo entra en una página no hay paginado. Si no, repackeamos con
    //    el ancho recortado por las flechas.
    const fullRight = bx + pw - pad;
    const pack = (rightX: number): number[] => {
      const pageOf: number[] = [];
      let cursor = startX;
      let page = 0;
      for (const { text } of chips) {
        if (cursor !== startX && cursor + text.width > rightX) {
          page++;
          cursor = startX;
        }
        pageOf.push(page);
        cursor += text.width + CHIP_GAP;
      }
      return pageOf;
    };

    let pageOf = pack(fullRight);
    this.chipPageCount = (pageOf[pageOf.length - 1] ?? 0) + 1;
    const paged = this.chipPageCount > 1;
    if (paged) {
      pageOf = pack(fullRight - ARROW_W * 2);
      this.chipPageCount = (pageOf[pageOf.length - 1] ?? 0) + 1;
    }

    // 3) Posicionar cada chip dentro de su página (todas arrancan en startX;
    //    sólo se muestra la página activa). Guardar {text, page}.
    const stripX = paged ? startX + ARROW_W : startX;
    const cursorByPage = new Map<number, number>();
    for (let i = 0; i < chips.length; i++) {
      const { text } = chips[i];
      const page = pageOf[i];
      const cursor = cursorByPage.get(page) ?? stripX;
      text.setX(cursor);
      cursorByPage.set(page, cursor + text.width + CHIP_GAP);
      this.filterChips.push({ text, page });
    }

    // 4) Flechas de paginado (sólo si hay más de una página).
    if (paged) {
      this.chipPage = Math.min(this.chipPage, this.chipPageCount - 1);
      const mkArrow = (glyph: string, ax: number, delta: number): Phaser.GameObjects.Text => {
        const arrow = this.scene.add
          .text(ax, chipY, glyph, withGameFont({ fontSize: '12px', color: '#ffffaa' }))
          .setOrigin(0, 0)
          .setPadding(2, 3, 2, 3)
          .setDepth(DEPTH_FG)
          .setScrollFactor(0)
          .setInteractive({ useHandCursor: true });
        arrow.on('pointerup', (p: Phaser.Input.Pointer) => {
          this.chipPage = Phaser.Math.Clamp(this.chipPage + delta, 0, this.chipPageCount - 1);
          this.applyChipPage();
          p.event.stopPropagation();
        });
        return arrow;
      };
      this.filterArrowLeft = mkArrow('‹', startX, -1);
      this.filterArrowRight = mkArrow('›', bx + pw - pad - ARROW_W + 2, 1);
    } else {
      this.chipPage = 0;
    }

    this.applyChipPage();
  }

  /** Muestra sólo los chips de la página activa y atenúa las flechas en los bordes. */
  private applyChipPage(): void {
    for (const { text, page } of this.filterChips) {
      text.setVisible(page === this.chipPage);
    }
    if (this.filterArrowLeft) {
      this.filterArrowLeft.setColor(this.chipPage > 0 ? '#ffffaa' : '#555555');
    }
    if (this.filterArrowRight) {
      this.filterArrowRight.setColor(
        this.chipPage < this.chipPageCount - 1 ? '#ffffaa' : '#555555'
      );
    }
  }

  // === FILTERING / RENDER ====================================================

  private filteredEntries(): SpellVendorEntryView[] {
    if (this.classFilter === ALL_CLASSES_FILTER) return this.currentEntries;
    return this.currentEntries.filter((e) => {
      const hasAllowed = !!e.allowedClasses && e.allowedClasses.length > 0;
      if (!hasAllowed) return true; // globales o forbidden-only siempre visibles
      return e.allowedClasses!.includes(this.classFilter);
    });
  }

  private handleRowClick(idx: number): void {
    const entries = this.filteredEntries();
    const entry = entries[this.scrollOffset + idx];
    if (!entry) return;
    this.selectedSpellId = entry.spellId;
    this.refreshList();
    this.refreshDetail();
  }

  private refreshList(): void {
    const entries = this.filteredEntries();
    for (let i = 0; i < this.visibleRows; i++) {
      const row = this.listRows[i];
      const entry = entries[this.scrollOffset + i];
      if (!entry) {
        row.bg.setVisible(false).setFillStyle(0x222222, 0);
        row.icon.setVisible(false);
        row.text.setVisible(false).setText('');
        row.learnedIcon.setVisible(false);
        continue;
      }
      row.bg.setVisible(true);
      row.icon.setVisible(true).setTexture(resolveSpellIcon(this.scene, entry.spellIcon)).setDisplaySize(THUMB_SIZE, THUMB_SIZE);
      row.text.setVisible(true).setText(entry.spellName);

      // Icono "ya aprendido" pegado a la derecha del nombre (gap 4px).
      if (entry.alreadyLearned) {
        row.learnedIcon
          .setVisible(true)
          .setPosition(row.text.x + row.text.width + 4, row.text.y + row.text.height / 2);
      } else {
        row.learnedIcon.setVisible(false);
      }

      const selected = this.selectedSpellId === entry.spellId;
      // Color rules (3 niveles):
      //  - alreadyLearned → blanco (ya lo tenés)
      //  - canLearn       → verde (lo podés aprender, aunque te falte oro/items)
      //  - !canLearn      → rojo  (gating duro: clase/raza/level)
      //  Selección pisa con bg amarilla, no cambia el color del texto.
      if (selected) {
        row.bg.setFillStyle(0x554422, 0.7);
      } else {
        row.bg.setFillStyle(0x222222, 0);
      }
      let color = '#ffffff';
      if (entry.alreadyLearned) color = '#ffffff';
      else if (entry.canLearn) color = '#77dd77';
      else color = '#dd6666';
      row.text.setColor(color);
    }
  }

  private refreshDetail(): void {
    const entry = this.currentEntries.find((e) => e.spellId === this.selectedSpellId);
    if (!entry) {
      this.detailIcon.setTexture(PLACEHOLDER_ICON).setDisplaySize(DETAIL_ICON_SIZE, DETAIL_ICON_SIZE);
      this.detailName.setText('');
      this.detailMeta.setText('');
      this.detailSpellInfo.removeAll(true);
      this.detailClassBadge.setText('');
      this.detailRaceBadge.setText('');
      this.detailPriceLabel.setText('');
      this.detailPriceAmount.setText('');
      this.detailDesc.setText('Seleccioná un hechizo de la lista.');
      this.clearItemCostIcons();
      this.learnBtn.setVisible(false);
      this.learnHint.setText('');
      return;
    }

    this.detailIcon.setTexture(resolveSpellIcon(this.scene, entry.spellIcon)).setDisplaySize(DETAIL_ICON_SIZE, DETAIL_ICON_SIZE);
    this.detailName.setText(entry.spellName);
    // Ya aprendido → blanco, sin bold (el spell ya esta "dominado", no necesita
    // enfasis). Aprendible o gateado → amarillo bold (default, llama la atencion).
    if (entry.alreadyLearned) {
      this.detailName.setColor('#ffffff').setFontStyle('normal');
    } else {
      this.detailName.setColor('#ffff88').setFontStyle('bold');
    }
    this.detailDesc.setText(entry.spellDescription);

    // Icono "ya aprendido" pegado a la derecha del nombre del detalle.
    if (entry.alreadyLearned) {
      this.detailLearnedIcon
        .setVisible(true)
        .setPosition(this.detailName.x + this.detailName.width + 6, this.detailName.y + this.detailName.height / 2);
    } else {
      this.detailLearnedIcon.setVisible(false);
    }

    // El estado "ya aprendido" ahora se representa con el icono al lado del
    // nombre — el meta line se reserva para info adicional futura.
    this.detailMeta.setText('');

    // Bloque de stats del spell (lookup local — el SpellDefinition vive en shared).
    const spellDef = getSpellDefinition(entry.spellId);
    this.rebuildSpellInfo(spellDef);

    // Badge de clase
    if (entry.allowedClasses && entry.allowedClasses.length > 0) {
      const names = entry.allowedClasses.map((c) => classLabel(c as CharacterClassId)).join(', ');
      this.detailClassBadge.setText(`Clases: ${names}`).setColor('#88aaff');
    } else if (entry.forbiddenClasses && entry.forbiddenClasses.length > 0) {
      const names = entry.forbiddenClasses.map((c) => classLabel(c as CharacterClassId)).join(', ');
      this.detailClassBadge.setText(`Prohibido para: ${names}`).setColor('#dd9966');
    } else {
      this.detailClassBadge.setText('Hechizo global (todas las clases)').setColor('#aaaaaa');
    }

    // Badge de raza — sólo se muestra si hay gating de raza. Sin filtro =
    // texto vacío (no spammear "todas las razas", ya se asume).
    if (entry.allowedRaces && entry.allowedRaces.length > 0) {
      const names = entry.allowedRaces.map((r) => raceLabel(r as CharacterRaceId)).join(', ');
      this.detailRaceBadge.setText(`Razas: ${names}`).setColor('#88aaff');
    } else if (entry.forbiddenRaces && entry.forbiddenRaces.length > 0) {
      const names = entry.forbiddenRaces.map((r) => raceLabel(r as CharacterRaceId)).join(', ');
      this.detailRaceBadge.setText(`Prohibido para razas: ${names}`).setColor('#dd9966');
    } else {
      this.detailRaceBadge.setText('');
    }

    // Precio: "Precio:" + [icono oro] + monto. El monto SIEMPRE va en dorado:
    // la affordance de "no podés pagar" se ve en el learnBtn (que pasa a
    // "[ Faltan recursos ]" en gris), no en el número de oro.
    this.detailPriceAmount.setText(formatThousands(entry.goldPrice)).setColor(GOLD_TEXT_COLOR);
    this.layoutDetailPrice();
    this.rebuildItemCostIcons(entry);

    // Botón
    const canAfford = this.canAffordEntry(entry);
    this.learnBtn.setVisible(true);
    if (entry.alreadyLearned) {
      this.learnBtn.setText('[ Ya aprendido ]').setColor('#ffffff');
      this.learnHint.setText('');
    } else if (!entry.canLearn) {
      this.learnBtn.setText('[ No disponible ]').setColor('#888888');
      this.learnHint.setText(entry.reasonIfNot ?? '');
    } else if (!canAfford) {
      this.learnBtn.setText('[ Faltan recursos ]').setColor('#888888');
      this.learnHint.setText('Conseguí oro y/o los items requeridos.');
    } else {
      this.learnBtn.setText('[ Aprender ]').setColor('#88ff88');
      this.learnHint.setText('');
    }
  }

  /**
   * Limpia y reconstruye los iconos de items requeridos del spell. Cada icono
   * tiene un badge "have/need" (verde si OK, rojo si falta) y un hover handler
   * que muestra el tooltip flotante del item.
   */
  private rebuildItemCostIcons(entry: SpellVendorEntryView): void {
    this.clearItemCostIcons();
    if (!entry.itemCosts || entry.itemCosts.length === 0) return;

    // Layout: fila horizontal arriba del learnBtn.
    const px = this.panelX;
    const py = this.panelY;
    const pw = this.pw;
    const ph = this.ph;
    const rx = px - pw / 2 + pw * 0.4 + 20; // mismo rx que detail pane
    const detW = pw - (pw * 0.4) - 30;
    const detCx = rx + detW / 2;
    // Posicionar arriba del learnBtn (a ~y = by+352) con margen suficiente
    // para el badge "have/need" abajo del icono.
    const startY = py + ph / 2 - 80;
    const iconSize = 28;
    const gap = 6;
    const totalW = entry.itemCosts.length * iconSize + (entry.itemCosts.length - 1) * gap;
    const startX = detCx - totalW / 2 + iconSize / 2;

    entry.itemCosts.forEach((cost: SpellVendorEntryView['itemCosts'][number], idx: number) => {
      const cx = startX + idx * (iconSize + gap);
      const cy = startY;
      const iconKey = `item_icon_${cost.itemIcon}`;
      const have = this.itemCountInInventory(cost.itemId);
      const ok = have >= cost.quantity;
      const tex = this.scene.textures.exists(iconKey) ? iconKey : PLACEHOLDER_ICON;
      const tmpl = getItemTemplate(cost.itemId);
      const rarity = tmpl ? nominalRarity(tmpl) : ItemRarity.COMMON;
      const rarityColor = ITEM_RARITY_COLORS[rarity as ItemRarity] ?? FALLBACK_RARITY_COLOR;

      // Fondo con borde de rareza por debajo del icono.
      const bgRect = this.scene.add
        .rectangle(cx, cy, iconSize + 4, iconSize + 4, 0x1a1a1a, 0.7)
        .setOrigin(0.5)
        .setStrokeStyle(1, rarityColor)
        .setDepth(DEPTH_FG - 1)
        .setScrollFactor(0);

      const image = this.scene.add
        .image(cx, cy, tex)
        .setDisplaySize(iconSize, iconSize)
        .setOrigin(0.5)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });

      const badge = this.scene.add
        .text(cx, cy + iconSize / 2 + 2, `${have}/${cost.quantity}`, withGameFont({
          fontSize: '9px', color: ok ? '#88ff88' : '#ff7777', fontStyle: 'bold',
          stroke: '#000000', strokeThickness: 2,
        }))
        .setOrigin(0.5, 0)
        .setDepth(DEPTH_FG + 1)
        .setScrollFactor(0);

      const showTooltip = (p: Phaser.Input.Pointer): void => {
        const footerText = `Tenés: ${have} / Necesitás: ${cost.quantity}`;
        this.tooltip.show(cost.itemId, p.x, p.y, {
          footerText,
          footerColor: ok ? '#88ff88' : '#ff7777',
        });
      };
      image.on('pointerover', showTooltip);
      image.on('pointermove', (p: Phaser.Input.Pointer) => {
        if (this.tooltip.isShown()) showTooltip(p);
      });
      image.on('pointerout', () => this.tooltip.hide());

      // Click sobre el icono: highlight el slot de inventario que tiene este
      // item (si lo tenés). El cliente decide qué hacer (flash, scroll, etc).
      image.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.callbacks.onHighlightInventorySlot?.(cost.itemId);
        p.event.stopPropagation();
      });

      // Guardar bgRect en el array para que se destruya y mueva con el resto.
      this.itemCostIcons.push({ image, badge, itemId: cost.itemId, qty: cost.quantity });
      // bgRect track separado para destrucción y move.
      this.itemCostBgRects.push(bgRect);
    });
  }

  private clearItemCostIcons(): void {
    for (const ic of this.itemCostIcons) {
      ic.image.destroy();
      ic.badge.destroy();
    }
    for (const r of this.itemCostBgRects) r.destroy();
    this.itemCostIcons = [];
    this.itemCostBgRects = [];
    this.tooltip.hide();
  }

  private itemCountInInventory(itemId: string): number {
    let total = 0;
    for (const inv of this.currentInventory) {
      if (inv.itemId === itemId) total += inv.quantity;
    }
    return total;
  }

  private canAffordEntry(entry: SpellVendorEntryView): boolean {
    if (entry.alreadyLearned || !entry.canLearn) return false;
    if (this.currentGold < entry.goldPrice) return false;
    for (const cost of entry.itemCosts) {
      if (this.itemCountInInventory(cost.itemId) < cost.quantity) return false;
    }
    return true;
  }

  /**
   * Reconstruye el container de stats del spell con una fila por stat
   * (sprite + text). Llamado en `refreshDetail` con el SpellDefinition
   * del entry seleccionado. Si `spell` es undefined, deja el container vacío.
   */
  private rebuildSpellInfo(spell: SpellDefinition | undefined): void {
    this.detailSpellInfo.removeAll(true);
    if (!spell) return;

    const ROW_H = 14;
    const ICON_SIZE = 12;
    const ICON_GAP = 4;
    const TEXT_STYLE = withGameFont({ fontSize: '10px', color: '#bbddff' });
    let rowIdx = 0;

    // Ficha iconográfica compartida con la pantalla de hechizos (spellInfoRows.ts):
    // una fila por característica presente (daño/cura, costos, casteo, cooldown, objetivo,
    // rango, ÁREA/AoE, control). Fuente única → ambos paneles muestran lo mismo.
    for (const row of getSpellInfoRows(spell)) {
      const y = rowIdx * ROW_H;
      const iconTex = getIconTexture(row.icon);
      let textX = 0;
      if (iconTex && this.scene.textures.exists(iconTex.textureKey)) {
        const sprite = this.scene.add
          .sprite(0, y + ICON_SIZE / 2, iconTex.textureKey, iconTex.frame)
          .setOrigin(0, 0.5)
          .setDisplaySize(ICON_SIZE, ICON_SIZE)
          .setScrollFactor(0);
        this.detailSpellInfo.add(sprite);
        textX = ICON_SIZE + ICON_GAP;
      }
      const txt = this.scene.add
        .text(textX, y + ICON_SIZE / 2, row.text, TEXT_STYLE)
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      this.detailSpellInfo.add(txt);
      rowIdx += 1;
    }
  }

  private updateGoldLabel(): void {
    this.goldLabelText.setText(formatThousands(this.currentGold));
  }

  /**
   * Posiciona label → ícono → amount en línea horizontal. Convención del oro:
   * el label tiene GAP con el ícono, pero el ícono y el monto van pegados
   * (sin gap) — se leen como una sola unidad "[oro] N".
   */
  private layoutDetailPrice(): void {
    const GAP = 4;
    const TIGHT = 0;
    let x = 0;
    this.detailPriceLabel.setPosition(x, 0);
    x += this.detailPriceLabel.width + GAP;
    this.detailPriceIcon.setPosition(x, 0);
    x += this.detailPriceIcon.displayWidth + TIGHT;
    this.detailPriceAmount.setPosition(x, 0);
  }

  // === LAYOUT HELPERS ========================================================

  private setVisible(visible: boolean): void {
    const elements: Phaser.GameObjects.GameObject[] = [
      this.bg, this.title, this.goldLabel, this.closeBtn,
      this.listBg, this.scrollUp, this.scrollDown,
      this.detailIcon, this.detailName, this.detailDesc, this.detailMeta,
      this.detailSpellInfo,
      this.detailClassBadge, this.detailRaceBadge, this.detailPrice,
      this.learnBtn, this.learnHint,
    ];
    for (const el of elements) {
      (el as unknown as { setVisible: (v: boolean) => unknown }).setVisible(visible);
    }
    // El icono de "ya aprendido" se controla por entry — al cerrar lo forzamos
    // false; al abrir lo deja refreshDetail/refreshList según corresponda.
    if (!visible) {
      this.detailLearnedIcon.setVisible(false);
    }
    for (const row of this.listRows) {
      row.bg.setVisible(visible);
      row.icon.setVisible(visible);
      row.text.setVisible(visible);
      if (!visible) row.learnedIcon.setVisible(false);
    }
    this.filterArrowLeft?.setVisible(visible);
    this.filterArrowRight?.setVisible(visible);
    if (visible) {
      this.applyChipPage();
    } else {
      for (const { text } of this.filterChips) text.setVisible(false);
    }
  }

  private movePanel(newX: number, newY: number): void {
    const dx = newX - this.panelX;
    const dy = newY - this.panelY;
    if (dx === 0 && dy === 0) return;
    this.panelX = newX;
    this.panelY = newY;
    const all: Array<{ x: number; y: number }> = [
      this.bg, this.title, this.goldLabel, this.closeBtn,
      this.listBg, this.scrollUp, this.scrollDown,
      this.detailIcon, this.detailName, this.detailLearnedIcon,
      this.detailDesc, this.detailMeta,
      this.detailSpellInfo,
      this.detailClassBadge, this.detailRaceBadge, this.detailPrice,
      this.learnBtn, this.learnHint,
      ...this.listRows.flatMap((r) => [r.bg, r.icon, r.text, r.learnedIcon]),
      ...this.filterChips.map((c) => c.text),
      ...(this.filterArrowLeft ? [this.filterArrowLeft] : []),
      ...(this.filterArrowRight ? [this.filterArrowRight] : []),
      ...this.itemCostIcons.flatMap((ic) => [ic.image, ic.badge]),
      ...this.itemCostBgRects,
    ];
    for (const el of all) {
      el.x += dx;
      el.y += dy;
    }
  }
}
